import React, { useContext, useState } from "react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import translations from "../../components/common/Translations";
import CommonTooltip from "../../components/common/CommonTooltip";
import {
  getCompareTargetFields,
  getCompareValidations,
  isCompareEligibleField,
} from "../../utils/workflowCompareValidation";

const TAB_VALIDATION = "validation";
const TAB_COMPARISON = "comparison";

const ValidationPreview = ({
  setCurrentValidation,
  setValidationModal,
  setValidationId,
  previewKey,
  validationId,
  validationFormValues,
  setValidationFormValues,
  preview,
  setViewValidationModal,
  viewValidationModal,
  droppedItems,
  setCompareValidationModal,
}) => {
  const { theme, langMode } = useContext(AppContext);
  // Two kinds of rule live on a field -- a check on its own value, and a check
  // against another field -- so the tab picks which one the buttons below act on
  // instead of stacking every button in one row.
  const [activeTab, setActiveTab] = useState(TAB_VALIDATION);

  const handleValidationModal = (uniqueKey) => {
    const currentItem = droppedItems.find((item) => item.uniqueKey === uniqueKey);

    if (
      preview.fieldType !== "text" &&
      preview.fieldType !== "textarea" &&
      preview.fieldType !== "number" &&
      preview.fieldType !== "email" &&
      preview.fieldType !== "file" &&
      preview.fieldType !== "multi_image" &&
      preview.fieldType !== "ocr"
    ) {
      toast.warn(translations[langMode].validationnotapplicable);
      return;
    }

    const existingValidations = currentItem?.validationValues || [];
    setCurrentValidation({
      validationId: Date.now(),
      validationCode: "",
      errorMessage: "",
      uniqueKey: uniqueKey,
    });

    setValidationFormValues(existingValidations);
    setValidationModal(true);
  };

  const handleValidationViewModal = () => {
    setViewValidationModal(true);
  };

  // A comparison needs a second field to point at, so say so up front rather
  // than opening a modal whose only content is an empty state.
  const handleCompareValidationModal = () => {
    if (getCompareTargetFields(droppedItems, previewKey).length === 0) {
      toast.warn(
        `${translations[langMode].compareValidationNoFields} ${translations[langMode].compareValidationEligibleTypes}`,
      );
      return;
    }
    setCompareValidationModal(true);
  };

  const currentValidationCount =
    droppedItems.find((item) => item.uniqueKey === previewKey)?.validationValues
      ?.length || 0;
  const hasValidationRules = currentValidationCount > 0;

  const compareValidationCount = getCompareValidations(
    droppedItems,
    previewKey,
  ).length;

  // Comparing two option pickers, photos or GPS fixes isn't meaningful, so the
  // Comparison tab isn't offered at all on those field types -- only on the ones
  // holding a single typed-in value.
  const canCompare = isCompareEligibleField(preview);
  const isComparisonTab = canCompare && activeTab === TAB_COMPARISON;

  const tabs = [
    {
      key: TAB_VALIDATION,
      label: translations[langMode].validationTabLabel,
      count: currentValidationCount,
    },
    ...(canCompare
      ? [
          {
            key: TAB_COMPARISON,
            label: translations[langMode].comparisonTabLabel,
            count: compareValidationCount,
          },
        ]
      : []),
  ];

  const primaryButtonClass =
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-[#236DB4] px-3 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-[#1B5690] focus:outline-none focus:ring-2 focus:ring-[#EAF3FB]";
  const secondaryButtonClass =
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#EAF3FB]";

  return (
    <div
      className={`mt-3 block max-w-sm rounded-lg border border-slate-200 bg-white p-3 ${
        theme === "dark"
          ? "dark-mode "
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
      }`}
    >
      <div className="space-y-2.5">
        {/* A lone tab is just a label, so the strip only appears once the field
            type actually has both kinds of rule available. */}
        {tabs.length > 1 && (
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] font-semibold transition-colors ${
                  activeTab === tab.key
                    ? "bg-white text-[#236DB4] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`rounded-full px-1.5 text-[10.5px] font-bold leading-4 ${
                      activeTab === tab.key
                        ? "bg-[#EAF3FB] text-[#236DB4]"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="text-center text-[12.5px] leading-[1.45] text-slate-500">
          {isComparisonTab
            ? compareValidationCount > 0
              ? `${compareValidationCount} ${translations[langMode].compareValidationCount}`
              : translations[langMode].compareValidationEmpty
            : hasValidationRules
              ? `${currentValidationCount} ${translations[langMode].validationCount}`
              : translations[langMode].validationEmpty}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {isComparisonTab ? (
            <CommonTooltip
              text={translations[langMode].compareValidationButton}
              description={translations[langMode].compareValidationTooltipDesc}
              position="top"
            >
              <button
                type="button"
                onClick={handleCompareValidationModal}
                className={primaryButtonClass}
              >
                {translations[langMode].compareValidationButton}
              </button>
            </CommonTooltip>
          ) : (
            <>
              <CommonTooltip
                text={translations[langMode].addValidationButton}
                description={translations[langMode].addValidationTooltipDesc}
                position="top"
              >
                <button
                  type="button"
                  onClick={() => {
                    handleValidationModal(previewKey);
                  }}
                  className={primaryButtonClass}
                >
                  {translations[langMode].addValidationButton}
                </button>
              </CommonTooltip>
              {hasValidationRules && (
                <CommonTooltip
                  text={translations[langMode].viewValidation}
                  description={translations[langMode].viewValidationTooltipDesc}
                  position="top"
                >
                  <button
                    type="button"
                    onClick={handleValidationViewModal}
                    className={secondaryButtonClass}
                  >
                    {translations[langMode].viewValidation}
                  </button>
                </CommonTooltip>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationPreview;
